/**
 * callActions.js
 * Talkroute
 *
 * Created by Vel Genov on 3/7/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */
import { REHYDRATE } from "redux-persist";
import { ThunkStatus } from "../models/Redux.models";

import {
  IDLE_CALL,
  MAKE_CALL,
  CALL_CONNECTED,
  HANG_UP,
  INCOMING_CALL,
  ACCEPT,
  CALL_CONNECTING,
  SET_OUTGOING_NUMBER,
  SET_NUMBER_TO_CALL,
  SET_IS_KEYPAD_VISIBLE,
  SET_IS_MUTE_MIC,
  SET_IS_CALL_HOLD,
  SET_DTMF_STRING,
  FETCH_BLOCKED_NUMBERS_SUCCESS,
  FETCH_BLOCKED_NUMBERS_FAILURE,
  FETCH_BLOCKED_NUMBERS_PENDING,
  SET_BLOCKED_TYPE,
  SET_LAST_CALLED_NUMBER,
  BLOCK_NUMBER_FAILURE,
  BLOCK_NUMBER_STARTED,
  BLOCK_NUMBER_SUCCESS,
  blockedTypes,
  SET_CALL_SESSION,
  UNBLOCK_NUMBER_FAILURE,
  UNBLOCK_NUMBER_STARTED,
  UNBLOCK_NUMBER_SUCCESS,
} from "../actions/callActions";
import { CallTypeEnum, CallStateEnum } from "../constants/callConstants";
import { Session } from "sip.js";

export interface ICallReducer {
  numberToCall: null | string;
  incomingCallNumber: null | string;
  dialedNumber: null | string;
  callNumber: null | string;
  defaultOutgoingPhoneNumber: null | string;
  outgoingPhoneNumber: null | string;
  fromPhoneNumber: null | string;
  connectedTime: null | number;
  isKeypadVisible: boolean;
  dtmfString: string;
  isMuteMic: boolean;
  isCallHold: boolean;
  callState: CallStateEnum;
  callType: null | string;
  blockedNumbers: string[];
  fetchBlockedNumbersPending: boolean;
  blockedType: blockedTypes;
  error: null | string;
  lastCalledNumber: string;
  lastCallOutgoingPhoneNumber: string;
  callSession: null | Session;
  blockNumberStatus: ThunkStatus;
  unblockNumberStatus: string;
  blockNumberError: any;
}

const initialState = {
  numberToCall: null, // The phone number to call
  incomingCallNumber: null, // There is an incoming call from this phone number
  dialedNumber: null, // There is an incoming call to this phone number
  callNumber: null, // On a call with with this phone number
  defaultOutgoingPhoneNumber: null, // The outgoing phone number to use when making calls
  outgoingPhoneNumber: null,
  fromPhoneNumber: null,
  connectedTime: null, // The timestamp when the call is connected
  isKeypadVisible: false, // The keypad is visible or not during calling
  dtmfString: "", // The string user entered during call
  isMuteMic: false, // Wheter the mic is mute or not
  isCallHold: false, // Wheter the call is hold or not when it's connected
  callState: CallStateEnum.IDLE,
  callType: null,
  blockedNumbers: [], // The array of call blocked numbers
  fetchBlockedNumbersPending: false,
  blockedType: blockedTypes.NONE, // Whether the call is blocked or unblocked
  error: null,
  lastCalledNumber: ``,
  lastCallOutgoingPhoneNumber: ``,
  callSession: null,
  blockNumberStatus: ThunkStatus.IDLE,
  unblockNumberStatus: "",
  blockNumberError: null,
};

/**
 * A redux function to process call actions
 * @param {*} state the redux state
 * @param {*} action passed in action through the callActions creator
 */
export default function callReducer(state: ICallReducer = initialState, action): ICallReducer {
  switch (action.type) {
    case REHYDRATE:
      // A redux-persist action. Called immediately after launch. Used to clear call data
      if (action.payload !== undefined) {
        return {
          ...state,
          numberToCall: null,
          callNumber: null,
          incomingCallNumber: null,
          dialedNumber: null,
          outgoingPhoneNumber: action.payload.callReducer.outgoingPhoneNumber,
          defaultOutgoingPhoneNumber:
            action.payload.callReducer.defaultOutgoingPhoneNumber,
          lastCalledNumber: action.payload.callReducer.lastCalledNumber,
          lastCallOutgoingPhoneNumber:
            action.payload.callReducer.lastCallOutgoingPhoneNumber,
          callState: CallStateEnum.IDLE,
          callType: null,
        };
      }

      return {
        ...state,
      };

    case IDLE_CALL:
      // Clear all call information
      return {
        ...state,
        callState: CallStateEnum.IDLE,
        numberToCall: null,
        callNumber: null,
        incomingCallNumber: null,
        dialedNumber: null,
        isKeypadVisible: false,
        isMuteMic: false,
        isCallHold: false,
        callType: null,
      };
    case MAKE_CALL: {
      // Make a call
      const { numberToCall, outgoingPhoneNumber } = action.payload;

      // Check if there is a number to be called
      // Don't attempt to make the call if the number is empty
      if (!numberToCall) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        numberToCall,
        callState: CallStateEnum.STARTING_CALL,
        incomingCallNumber: null,
        callType: CallTypeEnum.OUTGOING,
        outgoingPhoneNumber:
          outgoingPhoneNumber ?? state.defaultOutgoingPhoneNumber,
      };
    }
    case CALL_CONNECTING:
      // A call is currently connecting
      return {
        ...state,
        callState: CallStateEnum.CONNECTING,
      };
    case CALL_CONNECTED: {
      // A call is starting
      const curTimestamp = Date.now();

      return {
        ...state,
        numberToCall: null,
        callNumber: action.payload.phoneNumber,
        connectedTime: curTimestamp,
        dtmfString: "",
        callState: CallStateEnum.CONNECTED,
      };
    }

    case HANG_UP:
      // A call is ending
      return {
        ...state,
        numberToCall: null,
        callNumber: null,
        incomingCallNumber: null,
        dialedNumber: null,
        connectedTime: null,
        isKeypadVisible: false,
        isMuteMic: false,
        isCallHold: false,
        callState: CallStateEnum.IDLE,
      };

    case INCOMING_CALL:
      // Incoming call
      return {
        ...state,
        incomingCallNumber: action.payload.fromNumber,
        dialedNumber: action.payload.dialedNumber,
        callState: CallStateEnum.INCOMING,
        callType: CallStateEnum.INCOMING,
      };

    case ACCEPT:
      // ACCEPT an incoming call
      return {
        ...state,
        callState: CallStateEnum.ACCEPT,
      };

    case SET_OUTGOING_NUMBER: {
      // Update the outgoing phone number only if the call state is idle
      const { callState } = state;

      if (callState === CallStateEnum.IDLE) {
        // Set the outgoing phone number to use
        return {
          ...state,
          defaultOutgoingPhoneNumber: action.payload.outgoingPhoneNumber,
        };
      }

      return {
        ...state,
      };
    }

    case SET_NUMBER_TO_CALL: {
      // Update the numberToCall only if the call state is idle
      const { callState } = state;

      if (callState === CallStateEnum.IDLE) {
        // Set the numberToCall to use
        return {
          ...state,
          numberToCall: action.payload.numberToCall,
        };
      }

      return {
        ...state,
      };
    }

    case SET_IS_KEYPAD_VISIBLE:
      // Set the isKeypadVisible
      return {
        ...state,
        isKeypadVisible: action.payload.isKeypadVisible,
      };

    case SET_IS_MUTE_MIC: {
      // Set the isMuteMic
      const { callState } = state;
      const { isMuteMic } = action.payload;

      if (isMuteMic && callState === CallStateEnum.IDLE) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        isMuteMic,
      };
    }

    case SET_IS_CALL_HOLD: {
      // Set the isCallHold
      const { callState } = state;
      const { isCallHold } = action.payload;

      if (isCallHold && callState === CallStateEnum.IDLE) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        isCallHold,
      };
    }

    case SET_DTMF_STRING:
      // Set the dtmfString
      return {
        ...state,
        dtmfString: action.payload.dtmfString,
      };

    case FETCH_BLOCKED_NUMBERS_SUCCESS:
      // Set the blocked numbers
      return {
        ...state,
        blockedNumbers: action.payload.data,
        error: null,
      };

    case FETCH_BLOCKED_NUMBERS_FAILURE:
      // Set the error
      return {
        ...state,
        error: action.payload.error,
      };

    case BLOCK_NUMBER_STARTED:
      return {
        ...state,
        blockNumberStatus: ThunkStatus.PENDING,
        blockNumberError: null
      };

    case BLOCK_NUMBER_SUCCESS:
      return {
        ...state,
        blockNumberStatus: ThunkStatus.SUCCESS,
        blockNumberError: null
      };

    case BLOCK_NUMBER_FAILURE:
      // Set the error
      return {
        ...state,
        blockNumberStatus: ThunkStatus.ERROR,
        blockNumberError: action.payload
      };

    case UNBLOCK_NUMBER_STARTED:
      return {
        ...state,
        unblockNumberStatus: "started",
        blockNumberError: null
      };

    case UNBLOCK_NUMBER_SUCCESS:
      return {
        ...state,
        unblockNumberStatus: "success",
        blockNumberError: null
      };

    case UNBLOCK_NUMBER_FAILURE:
      // Set the error
      return {
        ...state,
        unblockNumberStatus: "failure",
        blockNumberError: action.payload
      };

    case SET_BLOCKED_TYPE:
      // Set the block type
      return {
        ...state,
        blockedType: action.payload.type,
      };

    case SET_LAST_CALLED_NUMBER:
      // Set the last called number
      return {
        ...state,
        lastCalledNumber: action.payload.phoneNumber,
        lastCallOutgoingPhoneNumber: action.payload.outgoingPhoneNumber,
      };

    case SET_CALL_SESSION:
      // Set the call session
      return {
        ...state,
        callSession: action.payload.callSession,
      };
    case FETCH_BLOCKED_NUMBERS_PENDING:
      return {
        ...state,
        fetchBlockedNumbersPending: action.payload,
      };
    default:
      // ALWAYS have a default case in a reducer
      return state;
  }
}
